import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { BulkConfirmResponse } from '../types';
import { formatRowErrors, getBulkConfirmStatusLabel } from '../utils';

interface BulkImportResultProps {
  result: BulkConfirmResponse;
  onBackToList: () => void;
  onNewImport: () => void;
}

function getResultMessage(result: BulkConfirmResponse): string {
  if (result.summary.created > 0 && result.summary.skipped === 0 && result.summary.failed === 0) {
    return 'Importação concluída com sucesso. Todos os usuários foram criados e receberão um convite por e-mail.';
  }
  if (result.summary.created > 0) {
    return 'Importação concluída parcialmente. Alguns usuários foram criados, enquanto outros foram ignorados ou apresentaram erro.';
  }
  return 'Nenhum usuário foi criado. Revise os erros apresentados e tente novamente.';
}

export function BulkImportResult({ result, onBackToList, onNewImport }: BulkImportResultProps) {
  return (
    <div className="space-y-6">
      <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-700">{getResultMessage(result)}</p>
        <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Criados</dt>
            <dd className="font-medium text-gray-900">{result.summary.created}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Ignorados</dt>
            <dd className="font-medium text-gray-900">{result.summary.skipped}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Com falha</dt>
            <dd className="font-medium text-gray-900">{result.summary.failed}</dd>
          </div>
        </dl>
        <p className="text-xs text-muted-foreground">
          Linhas ignoradas geralmente indicam e-mail duplicado ou repetido no lote.
        </p>
      </div>

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Linha</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Erros</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {result.rows.map((row, index) => (
              <TableRow key={`${row.email}-${row.rowNumber ?? index}`}>
                <TableCell>{row.rowNumber ?? '-'}</TableCell>
                <TableCell>{row.email}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      row.status === 'created' ? 'secondary' : row.status === 'failed' ? 'destructive' : 'outline'
                    }
                  >
                    {getBulkConfirmStatusLabel(row.status)}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-xs text-sm text-red-600">
                  {formatRowErrors(row.errors || []) || '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="space-y-3 md:hidden">
        {result.rows.map((row, index) => (
          <article key={`${row.email}-${row.rowNumber ?? index}`} className="space-y-2 rounded-md border border-gray-200 p-4">
            <p className="text-sm font-medium text-gray-900">{row.email}</p>
            <p className="text-sm text-gray-700">Linha: {row.rowNumber ?? '-'}</p>
            <Badge
              variant={row.status === 'created' ? 'secondary' : row.status === 'failed' ? 'destructive' : 'outline'}
            >
              {getBulkConfirmStatusLabel(row.status)}
            </Badge>
            {row.errors?.length > 0 && <p className="text-sm text-red-600">{formatRowErrors(row.errors)}</p>}
          </article>
        ))}
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onBackToList}>
          Voltar para usuários
        </Button>
        <Button type="button" onClick={onNewImport}>
          Importar outro CSV
        </Button>
      </div>
    </div>
  );
}
